import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthSession } from '@supabase/supabase-js';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { selectPermissions, selectSession, selectUserStatus } from '../store/auth/auth.selectors';
import { SupabaseService } from '../services/supabase.service';
import { mustChangePassword, resolveLandingRoute } from '../auth/password-gate';
import * as AuthActions from '../store/auth/auth.actions';

/** Mirrors permissionGuard's deadline, for the same reason. */
const PERMISSION_WAIT_MS = 5000;

/**
 * Guards /create-password.
 *
 * Signed out  -> /login
 * Gated       -> allow
 * Not gated   -> their normal landing route, so the screen stops being reachable
 *                the moment the password is set (and a stale bounce has
 *                somewhere to land rather than looping).
 */
export const createPasswordGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);
  const supabase = inject(SupabaseService);

  const decide = (session: AuthSession | null): Observable<boolean | UrlTree> => {
    if (!session) return of(router.createUrlTree(['/login']));
    if (mustChangePassword(session)) return of(true);

    // Flag already clear. Wait for permissions the same way permissionGuard does,
    // or we would send a still-rehydrating user to /no-permission purely because
    // their permissions had not arrived yet.
    return combineLatest([
      store.select(selectPermissions),
      store.select(selectUserStatus),
    ]).pipe(
      filter(([permissions]) => permissions !== null),
      take(1),
      map(([permissions, userStatus]) =>
        router.createUrlTree([resolveLandingRoute(permissions, userStatus)]),
      ),
      timeout({
        first: PERMISSION_WAIT_MS,
        with: () => of(router.createUrlTree(['/login'])),
      }),
      catchError(() => of(router.createUrlTree(['/login']))),
    );
  };

  return store.select(selectSession).pipe(
    take(1),
    switchMap((session) => {
      if (session) return decide(session);

      // Same fallback authGuard has. APP_INITIALIZER usually fills the store
      // before routing, but it swallows its own errors, so a cold load can reach
      // the router empty — without this, a hard refresh here ejects a signed-in
      // user to /login.
      return from(supabase.client.auth.getSession()).pipe(
        switchMap(({ data }) => {
          if (data.session) {
            store.dispatch(AuthActions.sessionLoaded({ session: data.session }));
          }
          return decide(data.session);
        }),
      );
    }),
  );
};
